import React, { useEffect, useState } from "react";
import { Radar, RefreshCw, AlertTriangle, TrendingUp, Info, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, StatCard, Badge, Button, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const PRIORITY_TONES = { critical: "danger", high: "accent", medium: "primary", low: "muted" };
const KIND_LABELS = { observation: "Observation", warning: "Warning", prediction: "Forecast", action: "Recommended action" };

export default function Intelligence() {
  const { has } = usePermissions();
  const canRecompute = has("admin:settings:manage");
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ module: "", priority: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filter.module) qs.append("module", filter.module);
      if (filter.priority) qs.append("priority", filter.priority);
      const [i, s] = await Promise.all([
        api.get(`/intelligence?${qs.toString()}`),
        api.get("/intelligence/summary"),
      ]);
      setInsights(i.data.insights || []); setSummary(s.data);
    } catch { setInsights([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function recompute() {
    setBusy(true);
    try { const r = await api.post("/intelligence/recompute"); toast.success(`Recomputed — ${r.data.inserted} new insights`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Recompute failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="intelligence-page">
      <PageHero
        eyebrow="Executive Intelligence"
        title="What's happening — and what to do about it"
        subtitle="Continuous analysis across every domain your role can see. Every insight includes observation, reason, impact, recommendation, priority and confidence."
        moduleKind="situation"
        actions={canRecompute && <Button variant="primary" onClick={recompute} disabled={busy} data-testid="intel-recompute-btn"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Recompute now</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label="Active insights" value={summary?.total_active ?? 0} icon={Sparkles} tone="primary" testid="intel-stat-total" />
        <StatCard label="Critical" value={summary?.critical ?? 0} icon={AlertTriangle} tone={summary?.critical > 0 ? "danger" : "muted"} testid="intel-stat-critical" />
        <StatCard label="High priority" value={summary?.high ?? 0} icon={TrendingUp} tone={summary?.high > 0 ? "accent" : "muted"} testid="intel-stat-high" />
        <StatCard label="Domains monitored" value={Object.keys(summary?.by_module || {}).length} icon={Radar} tone="info" testid="intel-stat-domains" />
      </div>

      <Section className="relative overflow-hidden" title="Insights" description="Filtered by your permissions." actions={
        <div className="flex gap-2 items-center">
          <select value={filter.module} onChange={e => setFilter(f => ({ ...f, module: e.target.value }))} className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs" data-testid="intel-filter-module">
            <option value="">All modules</option>
            <option value="poultry">Poultry</option>
            <option value="health">Health</option>
            <option value="finance">Finance</option>
            <option value="construction">Construction</option>
            <option value="inventory">Inventory</option>
            <option value="weather">Weather</option>
          </select>
          <select value={filter.priority} onChange={e => setFilter(f => ({ ...f, priority: e.target.value }))} className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs" data-testid="intel-filter-priority">
            <option value="">All priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      }>
        <Pattern kind="situation" />
        <div className="relative">
          {loading ? <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted/40 rounded-2xl" />)}</div>
          : insights.length === 0 ? <EmptyState icon={Info} title="No active insights" description="No signals to report — either data is being captured cleanly or the engine has not yet run. Admins can hit Recompute." />
          : (
            <ul className="space-y-3" data-testid="intel-list">
              {insights.map(i => (
                <li key={i.id} data-testid={`intel-item-${i.id}`} className={`rounded-2xl border border-border p-4 bg-card transition-colors hover:bg-muted/30`}>
                  <div className="flex items-start gap-3">
                    <Badge variant={PRIORITY_TONES[i.priority] || "muted"}>{i.priority}</Badge>
                    <Badge variant="neutral">{i.module}</Badge>
                    <Badge variant="info">{KIND_LABELS[i.kind] || i.kind}</Badge>
                    <span className="text-[11px] text-muted-foreground ml-auto">Confidence {i.confidence}%</span>
                  </div>
                  <div className="mt-2 font-heading text-base font-semibold">{i.observation}</div>
                  <div className="mt-2 grid sm:grid-cols-3 gap-3 text-sm">
                    <div><div className="eyebrow">Reason</div><div className="text-foreground/90">{i.reason || "—"}</div></div>
                    <div><div className="eyebrow">Impact</div><div className="text-foreground/90">{i.impact || "—"}</div></div>
                    <div><div className="eyebrow">Recommendation</div><div className="text-foreground/90 font-medium">{i.recommendation || "—"}</div></div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
    </div>
  );
}
